import { Observable, of } from "rxjs";
import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ServiceResult } from "@shared/helper/service-result";
import { TServiceResult } from "./../../helper/t-service-result";
import { LocalRepository } from "@shared/helper/local-repository";
import { HttpBaseService } from "../base_services/http_base.service";
import { PageList } from "./../../model/categoryAndSearch/pageList-model";
import { SuggestionModel } from "./../../model/authorModels/suggestiom-model";
import {
  BookEditionModel,
  BookModel,
  BookShoppingModel
} from "@shared/model/bookModels/book-model";
import { TermFilterModel } from "@shared/model/categoryAndSearch/term-filter-model";
import { ProductCategory } from "@shared/model/categoryAndSearch/product-category-model";
import { SearchFilterModel } from "@shared/model/categoryAndSearch/search-filter-model";
import { debounceTime } from "rxjs/operators";
import { addBookModel } from "@shared/model/bookModels/add-book-model";
import { languagesModel } from "@shared/model/bookModels/languages-model";
import { AddedBooklistAggrigatorModel } from "@app/pages/booklists/models/booklist-aggrigator-model";

@Injectable({
  providedIn: "root",
})
export class BookService extends HttpBaseService {
  category: ProductCategory[] = [];

  authorBooks: BookModel[] = [];

  constructor() {
    const repo = inject(LocalRepository);
    const http = inject(HttpClient);

    super(repo, http, "b");
  }

  getBookById(id: string): Observable<TServiceResult<BookModel>> {
    return this.http.get<TServiceResult<BookModel>>(
      `${this.apiUrl}/Get/${id} `
    );
  }

  getBookByISBN(
    isbn: string,
    TF?: TermFilterModel,
    id?: string
  ): Observable<TServiceResult<PageList<BookModel>>> {
    let paramsValues = "";
    if (TF?.pgNumber || TF?.pgNumber == 0)
      paramsValues = `?PgNumber=${TF?.pgNumber}`;
    if (TF?.pgSize) paramsValues = `?PgSize=${TF?.pgSize}`;
    if ((TF?.pgNumber || TF?.pgNumber == 0) && TF?.pgSize)
      paramsValues = `?PgNumber=${TF?.pgNumber}&PgSize=${TF?.pgSize}&bookId=${id}`;
    return this.http.get<TServiceResult<PageList<BookModel>>>(
      `${this.apiUrl}/GetByBookIsbn/${isbn}/${paramsValues}`
    );
  }

  getAllBooks(): Observable<TServiceResult<PageList<BookModel>>> {
    return this.http.get<TServiceResult<PageList<BookModel>>>(
      `${this.apiUrl}/GetAll`
    );
  }

  getBookByAuthorId(
    authorId: string,
    searchFilter?: SearchFilterModel
  ): Observable<TServiceResult<PageList<BookModel>>> {
    return this.http.post<TServiceResult<PageList<BookModel>>>(
      `${this.apiUrl}/GetByAuthorId/${authorId}`,
      searchFilter
    );
  }

  getBookByFilter(
    sf: SearchFilterModel
  ): Observable<TServiceResult<PageList<BookModel>>> {
    return this.http
      .post<TServiceResult<PageList<BookModel>>>(
        `${this.apiUrl}/GetByFilter`,
        sf
      )
      .pipe(debounceTime(1000));
  }

  searchBook(
    st: TermFilterModel
  ): Observable<TServiceResult<PageList<BookModel>>> {
    return this.http.get<TServiceResult<PageList<BookModel>>>(
      `${this.apiUrl}/Search/?searchTerm=${st.searchTerm}`
    );
  }

  createNewBook(book: BookModel): Observable<TServiceResult<string>> {
    return this.http.post<TServiceResult<string>>(
      `${this.apiUrl}/Create`,
      book
    );
  }

  CreateWithAuthorList(book: BookModel): Observable<TServiceResult<string>> {
    return this.http.post<TServiceResult<string>>(
      `${this.apiUrl}/CreateWithAuthorList`,
      book
    );
  }

  CreateBookEdition(
    book: BookEditionModel
  ): Observable<TServiceResult<string>> {
    return this.http.post<TServiceResult<string>>(
      `${this.apiUrl}/AddNewPrint`,
      book
    );
  }

  updateBook(book: BookModel): Observable<ServiceResult> {
    return this.http.put<ServiceResult>(`${this.apiUrl}/Update`, book);
  }

  deleteBookById(id: string): Observable<ServiceResult> {
    return this.http.get<ServiceResult>(`${this.apiUrl}/Delete/${id} `);
  }

  getAllCategories(): Observable<ProductCategory[]> {
    return of(this.category);
  }

  getByPublisherId(
    publisherId: string,
    searchFilter?: SearchFilterModel
  ): Observable<TServiceResult<PageList<BookModel>>> {
    return this.http.post<TServiceResult<PageList<BookModel>>>(
      `${this.apiUrl}/GetByPublisherId/${publisherId}`,
      searchFilter
    );
  }

  getAuthorTypes(): Observable<TServiceResult<addBookModel[]>> {
    return this.http.get<TServiceResult<addBookModel[]>>(
      `${this.apiUrl}/getAuthorTypes`
    );
  }

  getTracksForBooks(): Observable<TServiceResult<addBookModel[]>> {
    return this.http.get<TServiceResult<addBookModel[]>>(
      `${this.apiUrl}/getTracksForBooks`
    );
  }

  getCoverTypes(): Observable<TServiceResult<addBookModel[]>> {
    return this.http.get<TServiceResult<addBookModel[]>>(
      `${this.apiUrl}/getCoverTypes`
    );
  }

  getLanguages(): Observable<TServiceResult<languagesModel[]>> {
    return this.http.get<TServiceResult<languagesModel[]>>(
      `${this.apiUrl}/getLanguages`
    );
  }

  suggestion(
    sm: SuggestionModel
  ): Observable<TServiceResult<PageList<BookModel>>> {
    return this.http.post<TServiceResult<PageList<BookModel>>>(
      `${this.apiUrl}/Suggestion`,
      sm
    );
  }

  getMyAddedBooks(
    termFilter: TermFilterModel
  ): Observable<TServiceResult<PageList<BookModel>>> {
    return this.http.post<TServiceResult<PageList<BookModel>>>(
      `${this.apiUrl}/GetMyAddedBooks`,
      termFilter
    );
  }

  canEditBook(bookId: string): Observable<TServiceResult<boolean>> {
    return this.http.get<TServiceResult<boolean>>(
      `${this.apiUrl}/CanUpdate/${bookId}`
    );
  }

  getSharedBooksByBookId(
    bookId: string
  ): Observable<TServiceResult<PageList<BookShoppingModel>>> {
    return this.http.get<TServiceResult<PageList<BookShoppingModel>>>(
      `${this.apiUrl}/GetSharedBooksByBookId/${bookId}`
    );
  }


  GetMyAddedBooksWithDetails(termFilter: TermFilterModel): Observable<TServiceResult<PageList<AddedBooklistAggrigatorModel>>> {
    return this.http.post<TServiceResult<PageList<AddedBooklistAggrigatorModel>>>(`${this.apiUrl}/GetMyAddedBooksWithDetails`, termFilter);
  }

  bookSuggestionListByCategoryId(categoriesId: string[]): Observable<TServiceResult<PageList<BookModel>>> {
    return this.http.post<TServiceResult<PageList<BookModel>>>(`${this.apiUrl}/bookSuggestionListByCategoryId` , categoriesId)
  }

}
